import type { EmployeeAdvance, LoanMonthDeduction } from '@pms/shared';

/** Status pill colours, shared by the admin Loans tab, the staff Loans tab and My Loans. */
export const LOAN_STATUS_COLORS: Record<string, string> = {
  active: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  paused: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  closed: 'bg-slate-100 text-slate-500 dark:bg-muted dark:text-slate-400',
};

export const ADVANCE_STATUS_COLORS: Record<string, string> = {
  pending:   'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  deducted:  'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  cancelled: 'bg-slate-100 text-slate-500 dark:bg-muted dark:text-slate-400',
};

export interface StatusPill {
  label: string;
  cls: string;
}

export const monthKey = (year: number, month: number) => year * 12 + (month - 1);

export function currentMonthKey(): number {
  const now = new Date();
  return monthKey(now.getFullYear(), now.getMonth() + 1);
}

const RECOVERED  = 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300';
const THIS_MONTH = 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300';
const UPCOMING   = 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300';

/**
 * Nothing on the backend ever moves an advance to `deducted`: salary simply takes it from
 * its deduction month whatever the status (payroll.service computeMonthlySalary). So a
 * `pending` advance whose month has passed has in fact been recovered, and is shown as such.
 */
export function advanceDisplayStatus(adv: EmployeeAdvance, current: number): StatusPill {
  if (adv.status === 'cancelled') return { label: 'Cancelled', cls: ADVANCE_STATUS_COLORS.cancelled! };
  if (adv.status === 'deducted') return { label: 'Recovered', cls: RECOVERED };
  if (!adv.deduction_year || !adv.deduction_month) return { label: 'Pending', cls: ADVANCE_STATUS_COLORS.pending! };
  const key = monthKey(adv.deduction_year, adv.deduction_month);
  if (key < current) return { label: 'Recovered', cls: RECOVERED };
  if (key === current) return { label: 'This month', cls: THIS_MONTH };
  return { label: 'Upcoming', cls: UPCOMING };
}

/** Same rule as the admin Deduction Schedule (LoanScheduleModal); every ledger month deducts. */
export function deductionRowStatus(row: LoanMonthDeduction, current: number): StatusPill {
  const key = monthKey(row.year, row.month);
  if (row.source === 'payment') return { label: 'Paid manually', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' };
  if (key < current) return { label: 'Deducted', cls: RECOVERED };
  if (key === current) return { label: 'This month', cls: THIS_MONTH };
  return { label: 'Upcoming', cls: UPCOMING };
}
